import { useState } from "react";
import { toast } from "sonner";
import { MusicNotes, ArrowsClockwise } from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const MUSIC_PRESETS = [
  { label: "Tension strings", prompt: "Dark suspenseful string ostinato slowly building tension, cinematic thriller underscore, minimal percussion" },
  { label: "Epic trailer", prompt: "Epic orchestral trailer music with powerful brass, thundering drums and heroic choir, building to a massive climax" },
  { label: "Calm piano", prompt: "Gentle melancholic solo piano, slow and intimate, soft room ambience, emotional film scene underscore" },
  { label: "Playful cartoon", prompt: "Playful cartoon chase music, fast pizzicato strings, bouncy woodwinds, comedic percussion hits" },
];

export default function MusicGenerator({ onGenerated, credits }) {
  const { t } = useLang();
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState([20]);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (prompt.trim().length < 3) return;
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/music/generate", {
        prompt: prompt.trim(),
        duration_seconds: duration[0],
      });
      onGenerated?.(data);
      toast.success(t("musicDone"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Music generation failed");
      if (e.response?.status === 502) window.dispatchEvent(new CustomEvent("aurafx:engine-check"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="music-generator">
      <Textarea
        data-testid="music-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        maxLength={800}
        placeholder={t("musicPlaceholder")}
        className="min-h-[90px] bg-[color:var(--surface-2)] border-white/10 text-white placeholder:text-white/30 resize-none"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        {MUSIC_PRESETS.map((p) => (
          <button key={p.label} data-testid="music-preset-chip" onClick={() => setPrompt(p.prompt)}
            title={p.prompt}
            className="text-xs px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 transition-colors">
            {p.label}
          </button>
        ))}
      </div>
      <div className="mt-5 max-w-xs">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("duration")}</label>
          <span className="text-xs font-mono-num text-white/60">{duration[0]}s</span>
        </div>
        <Slider data-testid="music-duration-slider" value={duration} onValueChange={setDuration} min={10} max={60} step={5} />
      </div>
      <button
        data-testid="generate-music-btn"
        onClick={generate}
        disabled={generating || prompt.trim().length < 3 || credits < 3}
        className="btn-primary w-full mt-6 py-3.5 rounded-xl font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {generating ? (
          <><ArrowsClockwise size={18} className="animate-spin" /> {t("generating")}</>
        ) : (
          <><MusicNotes size={18} weight="fill" /> {t("generateMusic")} · 3 {t("creditsShort")}</>
        )}
      </button>
    </div>
  );
}
